import { Box, CircularProgress, MenuItem, Select } from '@material-ui/core'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useUser } from 'components/providers/UserProvider'
import allExportsImg from 'img/all-exports.png'
import {
  downloadCsvReport,
  downloadInventoryCsvReport,
  downloadOrderCsvReport,
  downloadTransactionCsvReport,
  fetchCsvReports,
  fetchInventoryCsvReports,
  fetchOrderCsvReports,
  fetchTransactionCsvReports,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import { gameMetaData } from 'shared'
import './exports-presenter.css'
import { EXPORT_TYPE_LABELS } from './TransactionsExports'

interface UnifiedExport {
  id: number
  type: 'taxation' | 'orders' | 'inventory' | 'transactions'
  createdAt: string
  exportEndedAt: string | null
  lastErrorAt: string | null
  intervalStartDate?: string
  intervalEndDate?: string
  params?: Record<string, string>
}

export const AllExports = (): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useUser()
  const [downloads, setDownloads] = useState<UnifiedExport[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)
  const [typeFilter, setTypeFilter] = useState<
    '' | 'taxation' | 'orders' | 'inventory' | 'transactions'
  >('')

  const filteredDownloads = typeFilter ? downloads.filter((d) => d.type === typeFilter) : downloads

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [taxRes, ordRes, invRes, trxRes] = await Promise.all([
        user.featureFlags?.taxation
          ? fetchCsvReports(currentPage).catch(() => ({ files: [], hasNextPage: false }))
          : Promise.resolve({ files: [], hasNextPage: false }),
        fetchOrderCsvReports(currentPage).catch(() => ({ files: [], hasNextPage: false })),
        fetchInventoryCsvReports(currentPage).catch(() => ({ files: [], hasNextPage: false })),
        fetchTransactionCsvReports(currentPage).catch(() => ({ files: [], hasNextPage: false })),
      ])

      setIsLastPage(
        !(taxRes.hasNextPage || ordRes.hasNextPage || invRes.hasNextPage || trxRes.hasNextPage)
      )

      const taxExports: UnifiedExport[] = (taxRes.files || []).map((f: any) => ({
        id: f.id,
        type: 'taxation' as const,
        createdAt: f.createdAt,
        exportEndedAt: f.exportEndedAt,
        lastErrorAt: f.lastErrorAt ?? null,
        intervalStartDate: f.intervalStartDate,
        intervalEndDate: f.intervalEndDate,
      }))

      const ordExports: UnifiedExport[] = (ordRes.files || []).map((f: any) => ({
        id: f.id,
        type: 'orders' as const,
        createdAt: f.createdAt,
        exportEndedAt: f.exportEndedAt,
        lastErrorAt: f.lastErrorAt ?? null,
        intervalStartDate: f.intervalStartDate,
        intervalEndDate: f.intervalEndDate,
        params: f.params,
      }))

      const invExports: UnifiedExport[] = (invRes.files || []).map((f: any) => ({
        id: f.id,
        type: 'inventory' as const,
        createdAt: f.createdAt,
        exportEndedAt: f.exportEndedAt,
        lastErrorAt: f.lastErrorAt ?? null,
        params: f.params,
      }))

      const trxExports: UnifiedExport[] = (trxRes.files || []).map((f: any) => ({
        id: f.id,
        type: 'transactions' as const,
        createdAt: f.createdAt,
        exportEndedAt: f.exportEndedAt,
        lastErrorAt: f.lastErrorAt ?? null,
        intervalStartDate: f.intervalStartDate,
        intervalEndDate: f.intervalEndDate,
        params: f.params,
      }))

      const all = [...taxExports, ...ordExports, ...invExports, ...trxExports].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )

      setDownloads(all)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [currentPage])

  const handleDownloadCsv = async (item: UnifiedExport) => {
    try {
      let res
      if (item.type === 'taxation') res = await downloadCsvReport(item.id)
      else if (item.type === 'orders') res = await downloadOrderCsvReport(item.id)
      else if (item.type === 'transactions') res = await downloadTransactionCsvReport(item.id)
      else res = await downloadInventoryCsvReport(item.id)

      const blob = new Blob([res.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${item.type}-report-${item.id}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'taxation':
        return t('exports.tabs.taxation')
      case 'orders':
        return t('exports.tabs.orders')
      case 'inventory':
        return t('exports.tabs.inventory')
      case 'transactions':
        return t('exports.tabs.transactions')
      default:
        return type
    }
  }

  const getParamsLabel = (item: UnifiedExport) => {
    if (item.type === 'inventory') {
      const idGame = item.params?.id_game || item.params?.idGame
      if (!idGame) return t('exports.allGames')
      const game = gameMetaData.find((g) => g.idGame.toString() === idGame.toString())
      return game ? game.name : idGame
    }

    if (item.type === 'orders') {
      const p = item.params || {}
      const parts: string[] = []
      const type = p.export_type || p.exportType
      if (type)
        parts.push(
          type === 'order_articles'
            ? t('exports.exportTypeOrderArticles')
            : t('exports.exportTypeOrders')
        )
      if (p.currency) parts.push(p.currency)
      const channel = p.sales_channel || p.salesChannel
      if (channel) parts.push(channel)
      return parts.length ? parts.join(' · ') : '—'
    }

    if (item.type === 'transactions') {
      const p = item.params || {}
      return `${t(`common.channels.${p.channel}`)} · ${t(
        EXPORT_TYPE_LABELS[p.exportType || p.export_type]
      )}`
    }

    return '—'
  }

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className, rowData }) => {
    rowData
    return (
      <div key={key} style={style} className={`${className}`}>
        {columns}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="tab-page-content">
        <div className="w-100 d-flex flex-column align-items-center mt-5">
          <CircularProgress size={60} />
        </div>
      </div>
    )
  }

  if (downloads.length === 0) {
    return (
      <div className="tab-page-content">
        <div className="w-100 d-flex flex-column align-items-center mt-3">
          <img src={allExportsImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
          <Box maxWidth={750} width="100%" px={2} className="text-center">
            <h2 className="fst-italic mt-3 text-center">{t('exports.noExportsYet')}</h2>
          </Box>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-page-content">
      <h2 className="mb-4">{t('exports.title')}</h2>
      <div className="mb-3" style={{ width: 200 }}>
        <Select
          fullWidth
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
          displayEmpty
        >
          <MenuItem value="">{t('exports.tabs.all')}</MenuItem>
          {user.featureFlags?.taxation && (
            <MenuItem value="taxation">{t('exports.tabs.taxation')}</MenuItem>
          )}
          <MenuItem value="inventory">{t('exports.tabs.inventory')}</MenuItem>
          <MenuItem value="orders">{t('exports.tabs.orders')}</MenuItem>
          <MenuItem value="transactions">{t('exports.tabs.transactions')}</MenuItem>
        </Select>
      </div>
      <div className="responsive-table-container">
        <AutoSizer disableHeight>
          {({ width: autoWidth }) => {
            const width = Math.max(autoWidth, 800)
            return (
              <Table
                width={width}
                height={40 + Math.max(40, filteredDownloads.length * 40)}
                headerHeight={40}
                rowHeight={40}
                rowCount={filteredDownloads.length}
                rowGetter={({ index }) => filteredDownloads[index]}
                rowRenderer={rowRenderer}
                overscanRowCount={5}
              >
                <Column
                  width={width * 0.12}
                  label={t('exports.type')}
                  dataKey="type"
                  cellRenderer={({ rowData: a }) => (
                    <div className="text-wrap">{getTypeLabel(a.type)}</div>
                  )}
                />
                <Column
                  width={width * 0.18}
                  label={t('exports.createdAt')}
                  dataKey="created"
                  cellRenderer={({ rowData: a }) => (
                    <div className="text-wrap">{new Date(a.createdAt).toLocaleString()}</div>
                  )}
                />
                <Column
                  width={width * 0.22}
                  label={t('exports.fromDate') + ' - ' + t('exports.toDate')}
                  dataKey="interval"
                  cellRenderer={({ rowData: a }) => {
                    if (!a.intervalStartDate) return <div>—</div>
                    return (
                      <div className="text-wrap">
                        {new Date(a.intervalStartDate).toLocaleDateString()} –{' '}
                        {new Date(a.intervalEndDate).toLocaleDateString()}
                      </div>
                    )
                  }}
                />
                <Column
                  width={width * 0.22}
                  label={t('exports.parameters')}
                  dataKey="params"
                  cellRenderer={({ rowData: a }) => (
                    <div className="text-wrap">{getParamsLabel(a)}</div>
                  )}
                />
                <Column
                  width={width * 0.1}
                  label={t('exports.status')}
                  dataKey="status"
                  headerClassName="centered-header"
                  cellRenderer={({ rowData: a }) => (
                    <Box textAlign={'center'}>
                      {a.lastErrorAt ? (
                        <i className="fa-light fa-circle-x error-color" />
                      ) : a.exportEndedAt ? (
                        <i className="fa-light fa-circle-check" style={{ color: 'green' }} />
                      ) : (
                        <CircularProgress size={16} />
                      )}
                    </Box>
                  )}
                />
                <Column
                  width={width * 0.12}
                  label={t('exports.download')}
                  dataKey="download"
                  headerClassName="centered-header"
                  cellRenderer={({ rowData: a }) => (
                    <Box textAlign={'center'}>
                      {!a.lastErrorAt && a.exportEndedAt ? (
                        <i
                          className="fa-light fa-arrow-down-to-bracket table-action-icon"
                          onClick={() => handleDownloadCsv(a)}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        '—'
                      )}
                    </Box>
                  )}
                />
              </Table>
            )
          }}
        </AutoSizer>
      </div>
      {(currentPage > 1 || !isLastPage) && (
        <TablePaginationControls
          currentPage={currentPage}
          setPage={setCurrentPage}
          isLastPage={isLastPage}
          startFromZero={false}
        />
      )}
    </div>
  )
}
